import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetView,
} from '@gorhom/bottom-sheet'
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
} from 'react'
import { Pressable, StyleSheet, type ViewStyle } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Grabber } from '../components/Grabber'
import type { NestedSheet } from '../hooks/useNestedSheet'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { StackedSheetBackground } from './FlowSheet'
import { sheetSizing, useInertScrollableFocus } from './sheetSizing'
import { useV2DrawerMotion } from './useV2DrawerMotion'

// Wider and lower than a flow sheet's, so the two read as different layers.
const GRABBER_WIDTH = 60
const GRABBER_TOP = 12
const GRABBER_BAR_HEIGHT = 5
const GRABBER_HIT_SLOP = 16

const TRANSPARENT_BACKDROP: ViewStyle = { backgroundColor: 'transparent' }

export interface DrawerProps<TScreen extends string> {
  sheet: NestedSheet<TScreen>
  /**
   * Span the window rather than sizing to the content. A drawer whose content
   * scrolls has to take a fixed detent, because a gorhom scrollable reports
   * only its own height (see `sheetSizing`).
   */
  fullHeight?: boolean
  children: ReactNode
}

/**
 * Every drawer that opens over the deposit sheet: the fee breakdown, the
 * country list and the KYC choosers all present through this one component, so
 * they are dismissed the same way and drawn on the same material.
 *
 * The drawer owns its grabber, drawn over the content rather than above it, so
 * it stays put while a list scrolls under it. A close button is the screen's
 * to draw where the design gives it one (the fee breakdown does); the gesture,
 * the grabber and the backdrop are the drawer's.
 */
export function Drawer<TScreen extends string>({
  sheet,
  fullHeight = false,
  children,
}: DrawerProps<TScreen>) {
  const { t } = useFunkitTranslation()
  const insets = useSafeAreaInsets()
  const animationConfigs = useV2DrawerMotion()
  const { nestedSheetRef, nestedScreen, setNestedScreen, closeNestedScreen } =
    sheet

  // gorhom fires `onDismiss` from its own close animation and nowhere else, so
  // a sheet that unmounts while open leaves the screen name set. The next press
  // would then set the same value, and the effect that presents it never runs.
  useEffect(() => () => setNestedScreen(null), [setNestedScreen])

  const renderBackdrop = useCallback(
    (props: ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        // The dismissal is the Pressable's, so the layer it wraps can carry an
        // accessible name — `pressBehavior` closes without announcing itself.
        pressBehavior="none"
        accessible={false}
        // No dim: the design (4006:4667) leaves the sheet under a drawer at
        // full brightness, and the drawer's glass refracts it. gorhom would
        // paint opaque black scaled by `opacity`, so the container clears to
        // transparent and the backdrop is only the tap target.
        opacity={1}
        style={TRANSPARENT_BACKDROP}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          accessibilityRole="button"
          accessibilityLabel={t('common.close')}
          onPress={closeNestedScreen}
        />
      </BottomSheetBackdrop>
    ),
    [closeNestedScreen, t],
  )

  return (
    <BottomSheetModal
      ref={nestedSheetRef}
      {...sheetSizing({ fullHeight })}
      topInset={insets.top}
      enablePanDownToClose
      // The drawer draws its own, over the content.
      handleComponent={null}
      backgroundComponent={StackedSheetBackground}
      backdropComponent={renderBackdrop}
      animationConfigs={animationConfigs}
      onDismiss={() => setNestedScreen(null)}
    >
      <BottomSheetView
        focusHook={fullHeight ? useInertScrollableFocus : undefined}
        // VoiceOver's two-finger scrub, which is how a screen reader leaves a
        // drawer whose other ways out are a gesture and a bar.
        onAccessibilityEscape={closeNestedScreen}
      >
        {children}
        {/* Last, so it paints over whatever scrolls beneath it. Absolutely
            positioned, so it costs the content no height and a dynamically
            sized drawer still measures to its own. Drawn for as long as the
            screen is, which includes the close it animates through. */}
        {nestedScreen === null ? null : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('common.close')}
            hitSlop={GRABBER_HIT_SLOP}
            onPress={closeNestedScreen}
            style={styles.grabber}
          >
            <Grabber width={GRABBER_WIDTH} top={GRABBER_TOP} />
          </Pressable>
        )}
      </BottomSheetView>
    </BottomSheetModal>
  )
}

const styles = StyleSheet.create({
  grabber: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: GRABBER_TOP * 2 + GRABBER_BAR_HEIGHT,
  },
})
